import { Injectable, inject, signal } from '@angular/core';

import { AuthService } from '../auth/auth.service';
import { ProfileApiService } from '../../features/profile/data/profile-api.service';
import { MediaReadUrlService } from '../../shared/data/media-read-url.service';
import { isMediaStorageKey } from '../../shared/utils/media-storage.util';

/** Caches the nutritionist profile photo for the header avatar (not stored in /me). */
@Injectable({ providedIn: 'root' })
export class ProfilePhotoService {
  private readonly auth = inject(AuthService);
  private readonly profileApi = inject(ProfileApiService);
  private readonly mediaReadUrl = inject(MediaReadUrlService);

  readonly photoUrl = signal<string | null>(null);

  private loaded = false;

  async ensureLoaded(): Promise<void> {
    if (this.loaded || !this.auth.isAuthenticated()) return;
    await this.refresh();
    this.loaded = true;
  }

  async refresh(): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      this.photoUrl.set(null);
      this.loaded = false;
      return;
    }
    try {
      const profile = await this.profileApi.getProfile();
      const key = profile.photo_key;
      if (!isMediaStorageKey(key)) {
        this.photoUrl.set(null);
        this.loaded = true;
        return;
      }
      const url = await this.mediaReadUrl.resolve(key);
      this.photoUrl.set(url);
      this.loaded = true;
    } catch {
      this.photoUrl.set(null);
    }
  }

  clear(): void {
    this.photoUrl.set(null);
    this.loaded = false;
  }
}
